import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { useIntl } from 'umi';
import type { ChangePasswordPayload, changePassword } from '@/services/global';

export interface ChangePasswordRef {
  show: () => void;
  hide: () => void;
}

interface ChangePasswordProps {
  request: typeof changePassword;
  loginOut: () => void;
}

type PasswordForm = ChangePasswordPayload & { confirmPassword: string };

const ChangePassword = forwardRef<ChangePasswordRef, ChangePasswordProps>(({ request, loginOut }, ref) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitting = useRef(false);
  const [form] = Form.useForm<PasswordForm>();
  const intl = useIntl();

  const close = () => {
    form.resetFields();
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    show: () => setOpen(true),
    hide: () => { if (!submitting.current) close(); },
  }));

  const onFinish = async ({ oldPassword, newPassword }: PasswordForm) => {
    if (submitting.current) return;
    submitting.current = true;
    setLoading(true);
    try {
      const response = await request({ oldPassword, newPassword });
      if (response.code !== '00000') {
        message.error(response.message || '修改密码失败，请稍后重试');
        return;
      }
      close();
      message.success(intl.formatMessage({ id: 'all.table.titleforSys3' }));
      loginOut();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '修改密码失败，请稍后重试');
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <Modal
      title={intl.formatMessage({ id: 'all.table.titleforSys4' })}
      open={open}
      onCancel={() => { if (!submitting.current) close(); }}
      footer={null}
      maskClosable={false}
      closable={!loading}
      keyboard={!loading}
    >
      <Form form={form} name="change-password" layout="vertical" onFinish={onFinish} disabled={loading}>
        <Form.Item
          name="oldPassword"
          label="原密码"
          rules={[{ required: true, message: '请输入原密码' }]}
        >
          <Input.Password placeholder="请输入原密码" autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label={intl.formatMessage({ id: 'all.table.titleforSys5' })}
          rules={[
            { required: true, whitespace: true, message: '请输入新密码，不能全为空白' },
            { min: 6, max: 32, message: '新密码长度须为6～32位' },
          ]}
        >
          <Input.Password placeholder="请输入6～32位新密码" autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label={intl.formatMessage({ id: 'all.table.titleforSys6' })}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: '请确认新密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                return !value || getFieldValue('newPassword') === value
                  ? Promise.resolve()
                  : Promise.reject(new Error(intl.formatMessage({ id: 'all.table.titleforSys2' })));
              },
            }),
          ]}
        >
          <Input.Password placeholder="请确认新密码" autoComplete="new-password" />
        </Form.Item>
        <div style={{ textAlign: 'right' }}>
          <Button onClick={close} disabled={loading} style={{ marginRight: 8 }}>
            {intl.formatMessage({ id: 'component.button.Cancel' })}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {intl.formatMessage({ id: 'component.button.sure' })}
          </Button>
        </div>
      </Form>
    </Modal>
  );
});

export default ChangePassword;
